/**
 * Notification Preferences API Module - Issue #319
 *
 * API functions for user notification preferences.
 *
 * #1606 SSOT note: this file mirrors NOTIFICATION_PREF_KEYS from
 * backend/src/schemas/notifications.ts. The FE/BE boundary forbids shared
 * imports — when adding a new pref column, update BOTH lists. Frontend tests
 * (frontend/src/pages/settings/NotificationSettingsPage.test.tsx) round-trip
 * the PATCH body via MSW, so drift surfaces in CI.
 */

import { apiGet, apiPatch, apiPost } from '../api';

// Canonical key list — mirrors backend NOTIFICATION_PREF_KEYS.
export const NOTIFICATION_PREF_KEYS = [
  'email_invites',
  'email_event_reminders',
  'email_rsvp_confirmations',
  'email_event_updates',
  'email_club_updates',
  'email_club_admin_activity',
  'email_digest',
  'sms_enabled',
  'sms_event_reminders',
  'sms_event_updates',
  'sms_club_updates',
  'sms_invites',
  'sms_rsvp_confirmations',
  'sms_club_admin_activity',
  'sms_digest',
  // Push channel — master gate (0001) + per-type buckets (0059, #2617; plus
  // push_club_updates from 0061, #1445 leader announcements). Mirrors backend
  // NOTIFICATION_PREF_KEYS. There is no push_digest (no push digest exists).
  'push_enabled',
  'push_event_reminders',
  'push_rsvp_confirmations',
  'push_event_updates',
  'push_club_updates',
  'push_club_admin_activity',
  'push_chat_messages',
] as const;

export type NotificationPrefKey = (typeof NOTIFICATION_PREF_KEYS)[number];

export type NotificationPreferences = Record<NotificationPrefKey, boolean> & {
  user_id: string;
  updated_at: number;
};

export type UpdateNotificationPreferencesRequest = Partial<
  Record<NotificationPrefKey, boolean>
>;

export interface NotificationPreferencesResponse {
  preferences: NotificationPreferences;
}

/**
 * Notification Preferences API functions
 */
export interface SendTestNotificationResponse {
  success: boolean;
  message: string;
}

export const notificationsApi = {
  /**
   * Get user's notification preferences
   */
  getPreferences: (): Promise<NotificationPreferencesResponse> => {
    return apiGet<NotificationPreferencesResponse>('/users/preferences/notifications');
  },

  /**
   * Update user's notification preferences
   */
  updatePreferences: (data: UpdateNotificationPreferencesRequest): Promise<NotificationPreferencesResponse> => {
    return apiPatch<NotificationPreferencesResponse>('/users/preferences/notifications', data);
  },

  /**
   * Send test notification email (#837)
   */
  sendTestNotification: (): Promise<SendTestNotificationResponse> => {
    return apiPost<SendTestNotificationResponse>('/users/preferences/notifications/test', {});
  },
};